import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import { ImageIcon } from '@repo/ui/icons'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { TranslationLocale } from '../../../lib/types/enums/translation/translation.enum'
import { TransportMeanInput } from '../../../lib/types/models/transport-means/transport-means-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { createTranslationsSchema } from '../../../lib/utils/validations/translations-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledFileInput from '../../elements/inputs/controlled-file-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import TranslationsInput from '../../elements/inputs/translations-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<TransportMeanInput> & {
  className?: string
}

const formSchema: ZodType<TransportMeanInput> = z.object({
  translations: createTranslationsSchema([TranslationLocale.FR]),
  code: createStringSchema('code'),
  picto: FILE_VALIDATION({ error: 'picto' }).nullish(),
  howManyQuarterBeforeDepartureForParents: createNumberSchema(
    'howManyQuarterBeforeDepartureForParents',
    { nullish: true }
  ),
  howManyQuarterBeforeArrivalForForeignProvider: createNumberSchema(
    'howManyQuarterBeforeArrivalForForeignProvider',
    { nullish: true }
  ),
  howManyQuarterAfterArrivalForForeignProvider: createNumberSchema(
    'howManyQuarterAfterArrivalForForeignProvider',
    { nullish: true }
  ),
  howManyQuarterBeforeDepartureForStaff: createNumberSchema(
    'howManyQuarterBeforeDepartureForStaff',
    { nullish: true }
  ),
  howManyQuarterBeforeDepartureForLEC: createNumberSchema('howManyQuarterBeforeDepartureForLEC', {
    nullish: true
  }),
  howManyQuarterBeforeDepartureForGroup: createNumberSchema(
    'howManyQuarterBeforeDepartureForGroup',
    { nullish: true }
  ),
  howManyQuarterBeforeArrivalForLEC: createNumberSchema('howManyQuarterBeforeArrivalForLEC', {
    nullish: true
  }),
  howManyQuarterBeforeArrivalForParents: createNumberSchema(
    'howManyQuarterBeforeArrivalForParents',
    { nullish: true }
  )
})

const TransportMeanForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<TransportMeanInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <TranslationsInput
            control={form.control}
            name="translations"
            label="NOM"
            requiredLocales={[TranslationLocale.FR]}
          />
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput required {...field} type="text" label="Code" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="howManyQuarterBeforeDepartureForLEC"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Aller : nombre de quarts d’heure avant le départ pour LEC/AAT`}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="howManyQuarterBeforeDepartureForStaff"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Aller : nombre de quarts d’heure avant le départ pour les encadrants`}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="howManyQuarterBeforeDepartureForParents"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Aller : nombre de quarts d’heure avant le départ pour les parents`}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* <FormField
            control={form.control}
            name="howManyQuarterBeforeArrivalForForeignProvider"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Nombre de quarts d’heure avant le départ pour le prestataire étranger`}
                  />
                </FormControl>
              </FormItem>
            )}
          /> */}
          <FormField
            control={form.control}
            name="howManyQuarterAfterArrivalForForeignProvider"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label="Aller : nombre de quarts d’heure après l’arrivée pour le prestataire étranger"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="howManyQuarterBeforeDepartureForGroup"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Retour : nombre de quarts d’heure avant le départ pour les groupes/AAT`}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="howManyQuarterBeforeArrivalForLEC"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Retour : nombre de quarts d’heure avant l’arrivée pour LEC/AAT`}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="howManyQuarterBeforeArrivalForParents"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label={`Retour : nombre de quarts d’heure avant l’arrivée pour les parents`}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="picto"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledFileInput
                    multiple
                    placeholder="Glisser-déposer ou parcourir"
                    types={[FileTypeEnum.IMAGE]}
                    icon={ImageIcon}
                    label={'Déposer des images'}
                    files={field?.value ? [field?.value] : undefined}
                    onFilesChange={(files) => {
                      field.onChange(files.length === 0 ? null : files[0])
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default TransportMeanForm
